import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Event = Database['public']['Tables']['events']['Row'];
type EventInsert = Database['public']['Tables']['events']['Insert'];

export interface EventAttendee {
  user_id: string;
  checked_in: boolean;
  profile: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    company: string | null;
  } | null;
}

export interface EventWithAttendees extends Event {
  attendees: EventAttendee[];
}

interface AttendeeProfile {
  id: string;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  company: string | null;
}

export function useEvents(cohortTag?: string) {
  return useQuery({
    queryKey: ['events', cohortTag],
    queryFn: async (): Promise<EventWithAttendees[]> => {
      // Fetch events
      let eventsQuery = supabase
        .from('events')
        .select('*')
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true });

      if (cohortTag && cohortTag !== 'all') {
        eventsQuery = eventsQuery.eq('cohort_tag', cohortTag);
      }

      const { data: events, error: eventsError } = await eventsQuery;
      if (eventsError) throw eventsError;
      if (!events || events.length === 0) return [];

      // Fetch all attendees for these events
      const eventIds = events.map(e => e.id);
      const { data: attendees, error: attendeesError } = await supabase
        .from('event_attendees')
        .select('*')
        .in('event_id', eventIds);

      if (attendeesError) throw attendeesError;

      // Get unique user IDs from attendees
      const userIds = [...new Set(attendees?.map(a => a.user_id) || [])];
      
      // Fetch profiles
      let profiles: AttendeeProfile[] = [];
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, user_id, full_name, avatar_url, company')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;
        profiles = (profilesData || []) as AttendeeProfile[];
      }

      // Create profile map
      const profileMap = new Map(profiles.map(p => [p.user_id, p]));

      // Create attendees map by event_id
      const attendeesMap = new Map<string, EventAttendee[]>();
      attendees?.forEach(a => {
        const profile = profileMap.get(a.user_id);
        const attendee: EventAttendee = {
          user_id: a.user_id,
          checked_in: a.checked_in,
          profile: profile ? {
            id: profile.id,
            full_name: profile.full_name,
            avatar_url: profile.avatar_url,
            company: profile.company,
          } : null
        };
        
        if (!attendeesMap.has(a.event_id)) {
          attendeesMap.set(a.event_id, []);
        }
        attendeesMap.get(a.event_id)!.push(attendee);
      });

      // Combine events with attendees
      return events.map(event => ({
        ...event,
        attendees: attendeesMap.get(event.id) || []
      }));
    },
  });
}

export function useEventAttendees(eventId: string) {
  return useQuery({
    queryKey: ['event-attendees', eventId],
    queryFn: async () => {
      const { data: attendees, error: attendeesError } = await supabase
        .from('event_attendees')
        .select('*')
        .eq('event_id', eventId);

      if (attendeesError) throw attendeesError;
      if (!attendees || attendees.length === 0) return [];

      const userIds = attendees.map(a => a.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, full_name, avatar_url, company, role')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]));

      return attendees.map(a => ({
        ...a,
        profile: profileMap.get(a.user_id) || null
      }));
    },
    enabled: !!eventId,
  });
}

export function useRSVPEvent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (eventId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('event_attendees')
        .insert({ event_id: eventId, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendees'] });
    },
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (event: Omit<EventInsert, 'created_by_user_id'>) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('events')
        .insert({ ...event, created_by_user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useCancelRSVP() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (eventId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('event_attendees')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendees'] });
    },
  });
}
